'use client';

import React from 'react';

import { cn } from '@/lib/utils';

interface LegendPayloadItem {
  value?: React.ReactNode;
  color?: string;
  dataKey?: string | number;
  /** Recharts copies each item's `legendType` here. */
  type?: string;
}

export interface ChartLegendProps {
  /** Injected by Recharts when passed as `<Legend content={<ChartLegend />} />`. */
  payload?: LegendPayloadItem[];
  className?: string;
}

/**
 * Legend for any chart with two or more series, so identity is never carried by
 * colour alone. The swatch carries the colour; the label wears a text token,
 * because coloured label text is the first thing to fail a contrast check.
 */
export default function ChartLegend({ payload, className }: ChartLegendProps) {
  // Recharts builds a payload entry for EVERY graphical item and only drops
  // `legendType="none"` inside its own DefaultLegendContent, so a custom
  // `content` like this one has to do the same filtering or an annotation
  // series shows up as a category it never was.
  const items = payload?.filter((item) => item.type !== 'none');
  if (!items?.length) return null;

  return (
    <ul
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-3',
        className
      )}
    >
      {items.map((item, index) => (
        <li
          key={`${String(item.dataKey ?? item.value)}-${index}`}
          className="flex items-center gap-1.5"
        >
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-sm"
            style={{ backgroundColor: item.color }}
          />
          <span className="ds-text-body-xs-medium text-ds-text-subtle">
            {item.value}
          </span>
        </li>
      ))}
    </ul>
  );
}
